import React, { FC } from 'react';
import { useGlobalSettings } from '../hooks/useGlobalSettings';
import { colors } from '../styles/tokens/colors';

export interface SeoDataProps {
  readonly metaAuthor?: string;
  readonly metaCopyright?: string;
  readonly h1?: string;
  readonly showOnSitemap: boolean | null;
  readonly remoteTypeName: string;
  readonly priority: number | null;
  readonly pageTitle: string | null;
  readonly pageDescription: string | null;
  readonly canonicalUrl: string | null;
  readonly allowCrawlersToIndex: boolean | null;
  readonly allowCrawlersToFollow: boolean | null;
}
export interface SeoProps {
  title?: string;
  meta?: [];
  image?: string;
  schema?: any[];
  canonical?: string;
  data?: SeoDataProps | null;
}

export const SEO: FC<SeoProps> = ({
  data,
  meta = [],
  title,
  schema = [],
  image,
  canonical,
}) => {
  // const { socialSettings, siteSettings, siteMeta } = useGlobalSettings();
  const { socialLinks, siteMeta, site } = useGlobalSettings();

  const inLanguage = 'en-GB';

  const sameAs: string[] = [];

  if (socialLinks?.twitter) {
    sameAs.push(socialLinks.twitter);
  }

  const companySchema = {
    '@context': 'http://schema.org',
    '@type': 'Organization',
    name: 'Trade Direct Insurance',
    url: 'https://www.tradedirectinsurance.co.uk/',
    logo: 'https://www.tradedirectinsurance.co.uk/logo.png',
    sameAs: [
      'https://www.instagram.com/tradedirectinsurance/',
      'https://en-gb.facebook.com/tradedirectinsurance/',
      'https://twitter.com/TradeDirectLtd',
      'https://www.youtube.com/channel/UCvsxatxuWk-VV-FcY8Gx5KQ',
      'https://www.linkedin.com/company/trade-direct-insurance-services-ltd/',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '0800 0318 497',
      contactType: 'customer service',
      areaServed: 'GB',
      availableLanguage: 'en',
    },
  };

  const insuranceCompanySchema = {
    '@context': 'http://schema.org',
    '@type': 'InsuranceAgency',
    name: 'Trade Direct Insurance',
    url: 'https://www.tradedirectinsurance.co.uk/',
    image:
      'https://www.tradedirectinsurance.co.uk/media/1643/dev-trade-direct-insurance-logo.png',
    email: 'enquiries@tradedirectinsurance.co.uk',
    telephone: '+448000280380',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Godalming',
      addressRegion: 'Surrey',
      postalCode: 'GU7 1EY',
      streetAddress: 'Mill Pool House, Mill Lane',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: 2118,
    },
  };

  const fullSchema = [companySchema, insuranceCompanySchema, ...schema];

  const verification: any[] = [
    {
      name: 'google-site-verification',
      content: siteMeta?.googleVerification,
    },
    {
      name: 'p:domain_verify',
      content: siteMeta?.pinterestVerification,
    },
  ];

  const metaTitle = title || data?.pageTitle || siteMeta?.metaTitleFallback;
  const metaDescription = data?.pageDescription ? data.pageDescription : '';
  const robotsIndex = data?.allowCrawlersToIndex ? 'index' : 'noIndex';
  const robotsFollow = data?.allowCrawlersToFollow ? 'follow' : 'nofollow';
  const brandColor = colors?.brandTwo100 || null;
  const canonicalUrl = data?.canonicalUrl || canonical;

  const metaItems = [
    {
      name: `robots`,
      content: `max-snippet:-1, max-image-preview:large, max-video-preview:-1, ${robotsIndex}, ${robotsFollow}`,
    },
    {
      name: `description`,
      content: metaDescription.replace(/<br\s*[/]?>/gi, '\n'),
    },
    {
      name: `author`,
      content: data?.metaAuthor || null,
    },
    {
      name: `copyright`,
      content: data?.metaCopyright || null,
    },
    {
      name: `theme-color`,
      content: brandColor,
    },
    {
      name: `msapplication-TileColor`,
      content: brandColor,
    },
    {
      name: `msapplication-navbutton-color`,
      content: brandColor,
    },
    {
      name: `apple-mobile-web-app-status-bar-style`,
      content: brandColor,
    },
    {
      property: `og:site_name`,
      content: siteMeta.companyName,
    },
    {
      property: `og:title`,
      content: metaTitle,
    },
    {
      property: `og:description`,
      content: metaDescription,
    },
    {
      property: `og:locale`,
      content: inLanguage.replace('-', '_'),
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      property: 'og:image',
      content: image,
    },
    {
      property: 'og:url',
      content: canonicalUrl,
    },
    {
      name: `twitter:card`,
      content: `summary`,
    },
    {
      name: `twitter:creator`,
      content: socialLinks?.twitter,
    },
    {
      name: `twitter:title`,
      content: data?.h1 || metaTitle,
    },
    {
      name: `twitter:description`,
      content: metaDescription,
    },
    {
      name: 'twitter:image',
      content: image,
    },
  ]
    .filter((m) => !!m.content)
    .concat(meta, verification);
  return (
    <>
      <title>{metaTitle}</title>
      {metaItems.map((metaItem, index) => (
        <React.Fragment key={index}>
          {metaItem?.name && (
            <meta
              name={metaItem?.name}
              content={metaItem?.content as string}
              key={index}
            />
          )}
          {metaItem?.property && (
            <meta
              property={metaItem?.property}
              content={metaItem?.content as string}
              key={index}
            />
          )}
        </React.Fragment>
      ))}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {fullSchema && (
        <script type="application/ld+json" id="json-schema">
          {JSON.stringify(
            fullSchema,
            undefined,
            process.env.NODE_ENV === 'development' ? 2 : 0
          )}
        </script>
      )}
    </>
  );
};
